import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { styled } from "@linaria/react";
import * as React from "react";
import { useState, useEffect, forwardRef, useRef, useCallback } from "react";
import { color } from "@creditornot/cb-ingredients/design-tokens";
import { Spinner } from "@creditornot/cb-progress";
import { createCssVars, assignInlineCssVars, getPortalElement, unsafeObjectKeys, clsx } from "@creditornot/cb-utils";
import { useFloating, shift, offset, flip, autoUpdate, useClick, useDismiss, useRole, useListNavigation, useTypeahead, useInteractions, FloatingPortal, FloatingFocusManager } from "@floating-ui/react";
import { typographyClassName } from "@creditornot/cb-ingredients";
import { MenuItemGroup, MenuActionItem } from "@creditornot/cb-menu";
import { usePopoverTransition, getClosedStyles, PopoverBase } from "@creditornot/cb-popover";
import { useDirection } from "@creditornot/cb-provider";
import "./style.css";
const rootClass$1 = "cb_FadeTransition_rootClass_rb7i";
const FadeTransition = ({
  children,
  show
}) => {
  const [render, setRender] = useState(show);
  useEffect(() => {
    if (show) {
      setRender(true);
    }
  }, [show]);
  const onAnimationEnd = () => {
    if (!show) {
      setRender(false);
    }
  };
  return render ? /* @__PURE__ */ jsx("div", {
    className: rootClass$1,
    "data-show": show ? "" : void 0,
    onAnimationEnd,
    children
  }) : null;
};
const sizeConfigs = {
  tiny: {
    minHeight: "24px",
    verticalPadding: "4px",
    horizontalPadding: "8px",
    stretchModeHorizontalPadding: "8px",
    iconMargin: "4px",
    iconWidth: "12px",
    spinnerSize: "14px"
  },
  small: {
    minHeight: "36px",
    verticalPadding: "8px",
    horizontalPadding: "16px",
    stretchModeHorizontalPadding: "16px",
    iconMargin: "8px",
    iconWidth: "12px",
    spinnerSize: "18px"
  },
  large: {
    minHeight: "56px",
    verticalPadding: "8px",
    horizontalPadding: "32px",
    stretchModeHorizontalPadding: "24px",
    iconMargin: "12px",
    iconWidth: "16px",
    spinnerSize: "24px"
  }
};
const cssVars$1 = createCssVars(["minHeight", "verticalPadding", "horizontalPadding", "stretchModeHorizontalPadding", "iconMargin", "iconWidth", "spinnerSize"]);
({
  primary: {
    color: color.textOnBgFillBrand,
    background: color.bgFillBrand,
    backgroundOnHover: color.bgFillBrandHovered,
    backgroundOnActive: color.bgFillBrandPressed,
    backgroundDisabled: color.bgFillBrandDisabled,
    colorDisabled: color.textOnBgFillBrandDisabled,
    fontWeight: "700"
  },
  primaryNegative: {
    color: color.textOnBgFillNegative,
    background: color.bgFillNegative,
    backgroundOnHover: color.bgFillNegativeHovered,
    backgroundOnActive: color.bgFillNegativePressed,
    backgroundDisabled: color.bgFillNegativeDisabled,
    colorDisabled: color.textOnBgFillNegativeDisabled,
    fontWeight: "700"
  },
  secondary: {
    color: color.textBrand,
    background: color.bgSurfaceBrand,
    backgroundOnHover: color.bgSurfaceBrandHovered,
    backgroundOnActive: color.bgSurfaceBrandPressed,
    backgroundDisabled: color.bgSurfaceBrandDisabled,
    colorDisabled: color.textBrandDisabled,
    fontWeight: "500"
  },
  secondaryNegative: {
    color: color.textNegative,
    background: color.bgSurfaceNegative,
    backgroundOnHover: color.bgSurfaceNegativeHovered,
    backgroundOnActive: color.bgSurfaceNegativePressed,
    backgroundDisabled: color.bgSurfaceNegativeDisabled,
    colorDisabled: color.textNegativeDisabled,
    fontWeight: "700"
  },
  inverse: {
    color: color.textSubdued,
    background: color.bgFillInverse,
    backgroundOnHover: color.bgFillInverseHovered,
    backgroundOnActive: color.bgFillInversePressed,
    backgroundDisabled: color.bgFillInverseDisabled,
    colorDisabled: color.textDisabled,
    fontWeight: "500"
  }
});
const _exp$2 = () => ({
  $visible
}) => $visible ? 1 : 0;
const _exp2 = () => ({
  $position
}) => $position === "start" ? "flex-start" : "flex-end";
const StretchModeIconContainer = /* @__PURE__ */ styled("div")({
  name: "StretchModeIconContainer",
  class: "cb_Button_StretchModeIconContainer_s7yn",
  propsAsIs: false,
  vars: {
    "s7yn0dk-0": [_exp$2()],
    "s7yn0dk-1": [_exp2()]
  }
});
const _exp5$2 = () => ({
  $position
}) => $position === "start" ? 0 : cssVars$1.iconMargin;
const _exp6$1 = () => ({
  $position
}) => $position === "end" ? 0 : cssVars$1.iconMargin;
const IconContainer$2 = /* @__PURE__ */ styled("div")({
  name: "IconContainer",
  class: "cb_Button_IconContainer_i1ii",
  propsAsIs: false,
  vars: {
    "i1ii3suy-0": [_exp5$2()],
    "i1ii3suy-1": [_exp6$1()]
  }
});
const _exp9 = () => ({
  $stretch
}) => $stretch ? "100%" : "auto";
const Root$2 = /* @__PURE__ */ styled("button")({
  name: "Root",
  class: "cb_Button_Root_rb3n",
  propsAsIs: false,
  vars: {
    "rb3n8vp-0": [_exp9()]
  }
});
const _exp28 = () => ({
  $visible
}) => $visible ? 1 : 0;
const _exp29 = () => ({
  $stretch,
  $withIcon
}) => {
  if ($stretch && $withIcon) {
    return `0px calc(${cssVars$1.iconWidth} * 1.5)`;
  }
  return "0px";
};
const Content = /* @__PURE__ */ styled("div")({
  name: "Content",
  class: "cb_Button_Content_c1dl",
  propsAsIs: false,
  vars: {
    "c1dloej8-0": [_exp28()],
    "c1dloej8-1": [_exp29()]
  }
});
const Label = /* @__PURE__ */ styled("div")({
  name: "Label",
  class: "cb_Button_Label_l1yk",
  propsAsIs: false
});
const SpinnerContainer = /* @__PURE__ */ styled("div")({
  name: "SpinnerContainer",
  class: "cb_Button_SpinnerContainer_s482",
  propsAsIs: false
});
const Button = React.forwardRef(({
  children,
  disabled = false,
  icon,
  iconPosition = "start",
  loading,
  onClick,
  role,
  size = "small",
  stretch = false,
  style,
  styleParams,
  type = "button",
  variant = "blue",
  ...restButtonProps
}, ref) => /* @__PURE__ */ jsxs(Root$2, {
  $stretch: stretch,
  "aria-busy": loading,
  "data-size": size,
  "data-variant": variant,
  disabled,
  onClick: disabled ? void 0 : onClick,
  role,
  style: styleParams ? {
    ...style,
    ...assignInlineCssVars(cssVars$1, styleParams)
  } : style,
  type,
  ...restButtonProps,
  ref,
  children: [/* @__PURE__ */ jsx(SpinnerContainer, {
    children: /* @__PURE__ */ jsx(FadeTransition, {
      show: !!loading,
      children: /* @__PURE__ */ jsx(Spinner, {
        size: "tiny",
        styleParams: {
          color: "currentColor",
          size: sizeConfigs[size].spinnerSize
        }
      })
    })
  }), stretch && /* @__PURE__ */ jsx(StretchModeIconContainer, {
    $position: iconPosition,
    $visible: !loading,
    children: icon
  }), /* @__PURE__ */ jsxs(Content, {
    $stretch: stretch,
    $visible: !loading,
    $withIcon: !!icon,
    children: [!stretch && !!icon && iconPosition === "start" && /* @__PURE__ */ jsx(IconContainer$2, {
      $position: iconPosition,
      children: icon
    }), /* @__PURE__ */ jsx(Label, {
      children
    }), !stretch && !!icon && iconPosition === "end" && /* @__PURE__ */ jsx(IconContainer$2, {
      $position: iconPosition,
      children: icon
    })]
  })]
}));
Button.displayName = "Button";
const Button$1 = Button;
const cssVars = createCssVars(["size", "iconSize", "width", "borderRadius"]);
const _exp$1 = () => ({
  $alignment
}) => $alignment === "boundingBox" ? 0 : `calc(-1 * (${cssVars.size} - ${cssVars.iconSize}) / 2)`;
const BG = /* @__PURE__ */ styled("div")({
  name: "BG",
  class: "cb_IconButton_BG_b1nm",
  propsAsIs: false,
  vars: {
    "b1nm3q3d-0": [_exp$1()]
  }
});
const IconContainer$1 = /* @__PURE__ */ styled("div")({
  name: "IconContainer",
  class: "cb_IconButton_IconContainer_i1x5",
  propsAsIs: false
});
const _exp5$1 = () => ({
  $size
}) => `${{
  tiny: 24,
  small: 36
}[$size]}px`;
const _exp6 = () => ({
  $alignment
}) => $alignment === "boundingBox" ? cssVars.size : `calc(${cssVars.size} / 2)`;
const Root$1 = /* @__PURE__ */ styled("button")({
  name: "Root",
  class: "cb_IconButton_Root_rnoe",
  propsAsIs: false,
  vars: {
    "rnoewfy-0": [_exp5$1()],
    "rnoewfy-1": [_exp6()]
  }
});
const IconButton = React.forwardRef(({
  alignment = "boundingBox",
  background = "solid",
  children: icon,
  size = "small",
  style,
  styleParams,
  type = "button",
  variant = "brand",
  ...rest
}, ref) => /* @__PURE__ */ jsxs(Root$1, {
  $alignment: alignment,
  $size: size,
  "data-bg": background,
  "data-variant": variant,
  style: styleParams ? {
    ...style,
    ...assignInlineCssVars(cssVars, styleParams)
  } : style,
  type,
  ...rest,
  ref,
  children: [/* @__PURE__ */ jsx(BG, {
    $alignment: alignment
  }), /* @__PURE__ */ jsx(IconContainer$1, {
    children: icon
  })]
}));
IconButton.displayName = "IconButton";
const IconButton$1 = IconButton;
const SvgCaretdown = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12.028 19.5c.662 0 1.292-.283 1.73-.778l9.81-11.13a1.84 1.84 0 00-.195-2.568 1.85 1.85 0 00-2.578.134l-8.579 9.732a.25.25 0 01-.376 0L3.262 5.158a1.849 1.849 0 00-3.17.609A1.84 1.84 0 00.49 7.592l9.807 11.126a2.312 2.312 0 001.732.782z" }) }));
const isMac = typeof navigator !== "undefined" && navigator.platform.includes("Mac");
const KEYMAP = {
  Alt: isMac ? "⌥" : "Alt",
  CmdOrCtrl: isMac ? "⌘" : "Ctrl",
  Control: "Ctrl",
  Shift: "⇧ Shift",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→"
};
const MODIFIER_KEYS = {
  Alt: "altKey",
  Control: "ctrlKey",
  Meta: "metaKey",
  Shift: "shiftKey"
};
const _exp = () => ({
  $stretch
}) => $stretch ? "100%" : "auto";
const Root = /* @__PURE__ */ styled("div")({
  name: "Root",
  class: "cb_SplitButton_Root_r1oy",
  propsAsIs: false,
  vars: {
    "r1oyiev5-0": [_exp()]
  }
});
const _exp4 = () => Button$1;
const PrimaryButton = /* @__PURE__ */ styled(_exp4())({
  name: "PrimaryButton",
  class: "cb_SplitButton_PrimaryButton_p1ni",
  propsAsIs: true
});
const _exp5 = () => Button$1;
const Toggle = /* @__PURE__ */ styled(_exp5())({
  name: "Toggle",
  class: "cb_SplitButton_Toggle_txkh",
  propsAsIs: true
});
const _exp7 = () => PopoverBase;
const Menu = /* @__PURE__ */ styled(_exp7())({
  name: "Menu",
  class: "cb_SplitButton_Menu_mmq0",
  propsAsIs: true
});
const Kbd = /* @__PURE__ */ styled("kbd")({
  name: "Kbd",
  class: "cb_SplitButton_Kbd_kz2s",
  propsAsIs: false
});
const Shortcut = ({
  keys
}) => {
  const displayedKeys = (Array.isArray(keys) ? keys : [keys]).map((key) => {
    return key in KEYMAP ? KEYMAP[key] : key;
  });
  return /* @__PURE__ */ jsx(Fragment, {
    children: displayedKeys.map((key, index) => /* @__PURE__ */ jsx(Kbd, {
      className: typographyClassName.Small,
      children: key
    }, index))
  });
};
const checkExactModifiersArePressed = (event, keyCodes) => {
  const modifiers = unsafeObjectKeys(MODIFIER_KEYS);
  return !modifiers.some((modifier) => keyCodes.includes(modifier) && !event[MODIFIER_KEYS[modifier]] || !keyCodes.includes(modifier) && event[MODIFIER_KEYS[modifier]]);
};
const matchesShortcut = (event, shortcut) => {
  if (!shortcut)
    return false;
  const keyCodes = (Array.isArray(shortcut) ? shortcut : [shortcut]).map((key) => {
    if (/^\d$/.test(key))
      return `Digit${key}`;
    if (/^[A-Z]$/.test(key))
      return `Key${key}`;
    if (key === "CmdOrCtrl")
      return isMac ? "Meta" : "Control";
    return key;
  });
  return keyCodes.includes(event.code) && checkExactModifiersArePressed(event, keyCodes);
};
const useKeyboardShortcuts = ({
  enabled,
  handleSelect,
  menuItems
}) => {
  useEffect(() => {
    const shortcutActions = menuItems.filter((item) => item.shortcut);
    if (enabled && shortcutActions.length) {
      const listener = (event) => {
        shortcutActions.forEach(({
          onClick,
          shortcut
        }) => {
          if (matchesShortcut(event, shortcut))
            handleSelect(onClick);
        });
      };
      document.addEventListener("keydown", listener);
      return () => {
        document.removeEventListener("keydown", listener);
      };
    }
    return;
  }, [handleSelect, menuItems, enabled]);
};
const SplitButton = React.forwardRef(({
  disabled = false,
  keyboardShortcutHandlers = "global",
  menuButtonAriaLabel = "Other actions",
  menuItems,
  menuPortalElement = getPortalElement(),
  role: primaryButtonRole,
  size = "small",
  stretch = false,
  style,
  styleParams,
  type = "button",
  variant = "primary",
  ...rest
}, ref) => {
  const rtl = useDirection() === "rtl";
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const listRef = useRef([]);
  const listContentRef = useRef(menuItems.map((item) => item.label));
  const isTypingRef = useRef(false);
  const {
    context,
    placement,
    refs,
    strategy,
    x,
    y
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom-end",
    middleware: [shift(), offset({
      mainAxis: 4
    }), flip()],
    whileElementsMounted: autoUpdate
  });
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const listRole = useRole(context, {
    role: "menu"
  });
  const keyboardShortcutsEnabled = keyboardShortcutHandlers === "global" || keyboardShortcutHandlers === "whenInFocus" && isFocused;
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    rtl
  });
  const placementSide = placement.split("-")[0];
  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    selectedIndex,
    onMatch: isOpen ? setActiveIndex : setSelectedIndex,
    onTypingChange(isTyping) {
      isTypingRef.current = isTyping;
    }
  });
  const {
    getFloatingProps,
    getItemProps,
    getReferenceProps
  } = useInteractions([click, dismiss, listRole, listNav, typeahead]);
  const {
    isMounted,
    styles
  } = usePopoverTransition(context);
  const handleSelect = useCallback((callback) => {
    setIsOpen(false);
    callback();
  }, []);
  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(null);
    }
  }, [isOpen]);
  useEffect(() => {
    listContentRef.current = menuItems.map((item) => item.label);
  }, [menuItems]);
  useKeyboardShortcuts({
    menuItems,
    enabled: keyboardShortcutsEnabled,
    handleSelect
  });
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [/* @__PURE__ */ jsxs(Root, {
      $stretch: stretch,
      "data-disabled": disabled ? "" : void 0,
      "data-variant": variant,
      onBlur: () => setIsFocused(isOpen),
      onFocus: () => setIsFocused(true),
      style,
      children: [/* @__PURE__ */ jsx(PrimaryButton, {
        disabled,
        role: primaryButtonRole,
        size,
        styleParams,
        type,
        variant,
        ...rest,
        ref
      }), /* @__PURE__ */ jsx(Toggle, {
        "aria-label": menuButtonAriaLabel,
        disabled,
        size,
        styleParams: {
          horizontalPadding: "16px",
          ...styleParams
        },
        variant,
        ...getReferenceProps({
          ref: refs.setReference
        }),
        children: /* @__PURE__ */ jsx(SvgCaretdown, {})
      })]
    }), /* @__PURE__ */ jsx(FloatingPortal, {
      root: menuPortalElement,
      children: isMounted && /* @__PURE__ */ jsx(FloatingFocusManager, {
        context,
        modal: false,
        children: /* @__PURE__ */ jsx(Menu, {
          style: {
            // floating-ui doesn't seem to resolve initial styles properly, thus this
            ...getClosedStyles({
              side: placementSide
            }),
            ...styles,
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            overflowY: "auto"
          },
          ...getFloatingProps({
            ref: refs.setFloating
          }),
          children: /* @__PURE__ */ jsx(MenuItemGroup, {
            children: menuItems.map(({
              disabled: disabled2,
              label,
              onClick,
              shortcut,
              variant: variant2 = "black",
              ...menuItemProps
            }, index) => /* @__PURE__ */ jsx(MenuActionItem, {
              variant: variant2,
              ...menuItemProps,
              "aria-current": index === activeIndex ? true : void 0,
              focused: index === activeIndex,
              ref: (node) => {
                listRef.current[index] = node;
              },
              rightContent: shortcut && /* @__PURE__ */ jsx(Shortcut, {
                keys: shortcut
              }),
              role: "menuitem",
              tabIndex: index === activeIndex ? 0 : -1,
              ...getItemProps({
                disabled: disabled2,
                // Handle pointer select.
                onClick() {
                  handleSelect(onClick);
                },
                // Handle keyboard select.
                onKeyDown(event) {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (!keyboardShortcutsEnabled || menuItems.every((item) => !matchesShortcut(
                      // TODO: remove type assertion once React 16 support is dropped, see https://github.com/facebook/react/pull/18287
                      event,
                      item.shortcut
                    ))) {
                      handleSelect(onClick);
                    }
                  }
                  if (event.key === " " && !isTypingRef.current) {
                    event.preventDefault();
                  }
                },
                onKeyUp(event) {
                  if (event.key === " " && !isTypingRef.current) {
                    handleSelect(onClick);
                  }
                }
              }),
              children: label
            }, index))
          })
        })
      })
    })]
  });
});
SplitButton.displayName = "SplitButton";
const SplitButton$1 = SplitButton;
const sizeCssVars = createCssVars(["iconSize"]);
const rootClass = "cb_TextButton_rootClass_r1vf";
const DisabledButtonOnMouseLeaveFix = /* @__PURE__ */ styled("div")({
  name: "DisabledButtonOnMouseLeaveFix",
  class: "cb_TextButton_DisabledButtonOnMouseLeaveFix_d1xp",
  propsAsIs: false
});
const _exp12 = () => ({
  $position
}) => $position === "start" ? 0 : 8;
const _exp13 = () => ({
  $position
}) => $position === "end" ? 0 : 8;
const IconContainer = /* @__PURE__ */ styled("span")({
  name: "IconContainer",
  class: "cb_TextButton_IconContainer_i1y4",
  propsAsIs: false,
  vars: {
    "i1y4k66l-0": [_exp12(), "px"],
    "i1y4k66l-1": [_exp13(), "px"]
  }
});
const TextButtonContent = ({
  children,
  icon,
  iconPosition
}) => /* @__PURE__ */ jsxs(Fragment, {
  children: [!!icon && iconPosition === "start" && /* @__PURE__ */ jsx(IconContainer, {
    $position: iconPosition,
    children: icon
  }), children, !!icon && iconPosition === "end" && /* @__PURE__ */ jsx(IconContainer, {
    $position: iconPosition,
    children: icon
  })]
});
const TextButton = React.forwardRef(({
  children,
  className,
  disableTextSelect = false,
  fontFamily = "default",
  icon,
  iconPosition = "start",
  size = "small",
  style,
  styleParams,
  type = "button",
  variant = "brand",
  wrap = false,
  ...restButtonProps
}, ref) => /* @__PURE__ */ jsxs("button", {
  className: clsx(rootClass, className, {
    [typographyClassName.bodyBase]: size === "inherit",
    [typographyClassName.Body3]: size === "small",
    [typographyClassName.Body2]: size === "medium",
    [typographyClassName.Body1]: size === "large"
  }),
  "data-font-inherit": fontFamily === "inherit" ? "" : void 0,
  "data-no-text-select": disableTextSelect ? "" : void 0,
  "data-size": size,
  "data-variant": variant,
  "data-wrap": wrap ? "" : void 0,
  style: {
    ...(styleParams ? assignInlineCssVars(sizeCssVars, styleParams) : null),
    ...style
  },
  type,
  ...restButtonProps,
  ref,
  children: [/* @__PURE__ */ jsx(DisabledButtonOnMouseLeaveFix, {}), /* @__PURE__ */ jsx(TextButtonContent, {
    icon,
    iconPosition,
    children
  })]
}));
TextButton.displayName = "TextButton";
const TextButton$1 = TextButton;
const link = "cb_TextButtonLink_link_l1p4";
const TextButtonLink = React.forwardRef(({
  children,
  className,
  disableTextSelect = false,
  fontFamily = "default",
  icon,
  iconPosition = "start",
  size = "small",
  style,
  styleParams,
  variant = "brand",
  wrap = true,
  ...rest
}, ref) => /* @__PURE__ */ jsx("a", {
  className: clsx(rootClass, link, className, {
    [typographyClassName.bodyBase]: size === "inherit",
    [typographyClassName.Body3]: size === "small",
    [typographyClassName.Body2]: size === "medium",
    [typographyClassName.Body1]: size === "large"
  }),
  "data-font-inherit": fontFamily === "inherit" ? "" : void 0,
  "data-no-text-select": disableTextSelect ? "" : void 0,
  "data-size": size,
  "data-variant": variant,
  "data-wrap": wrap ? "" : void 0,
  style: {
    ...(styleParams ? assignInlineCssVars(sizeCssVars, styleParams) : null),
    ...style
  },
  ...rest,
  ref,
  children: /* @__PURE__ */ jsx(TextButtonContent, {
    icon,
    iconPosition,
    children
  })
}));
TextButtonLink.displayName = "TextButtonLink";
const TextButtonLink$1 = TextButtonLink;
export {
  Button$1 as Button,
  IconButton$1 as IconButton,
  SplitButton$1 as SplitButton,
  TextButton$1 as TextButton,
  TextButtonLink$1 as TextButtonLink
};
//# sourceMappingURL=index.js.map
